import React from "react";
import { observer } from "mobx-react-lite";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import {
  FooterButton,
  FooterContainer,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import styled from "@emotion/styled";
import { APP_CONFIG } from "@/domains/global/config";
import { useAppStore } from "@/store";

export const IntroVideoStep: React.FC<StepRenderProps> = observer(({ onClose, onNavToStep }) => {
  const { store } = useAppStore();

  const hasMeetingSystemPermissionsStepNext =
    store.platformInfo.perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions;
  const hasVoiceSystemPermissionsStepNext =
    store.platformInfo.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions;
  const hasAnyNextSteps = hasMeetingSystemPermissionsStepNext || hasVoiceSystemPermissionsStepNext;

  const handleNext = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    // We mark this section of onboarding as complete
    onClose?.();

    // First, check if we should go to the meeting permissions steps...
    if (hasMeetingSystemPermissionsStepNext) {
      onNavToStep?.("system-permissions-meeting-intro");
      return;
    }

    // ...otherwise, we check if we should go to the voice permissions steps
    if (hasVoiceSystemPermissionsStepNext) {
      onNavToStep?.("system-permissions-voice-permissions");
      return;
    }
  };

  return (
    <WizardContentWrapper style={{ height: "460px" }}>
      <StepTitle>Get started with Mem in 5 minutes</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Learn how to make the most of your new AI thought partner
        </MDSText>

        <StyledVideoWrapper>
          <IntroVideo controls preload="auto">
            <source src={APP_CONFIG.URLS.INTRO_VIDEO_ASSET_URL} type="video/mp4" />
            Your browser does not support the video tag.
          </IntroVideo>
        </StyledVideoWrapper>
      </StepContent>

      <FooterContainer>
        <FooterButton onClick={handleNext}>{hasAnyNextSteps ? "Next" : "Finish"}</FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

const StyledVideoWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IntroVideo = styled.video`
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  height: 315px;
  width: 560px;
`;
